import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FileText, Download, MessageSquare, User, Clock, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import ChatBox from '../Chat/ChatBox';
import { parseUTC, formatDate } from '../../lib/date';

const MyOrders = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const { ordersAPI } = await import('../../api');
      const data = await ordersAPI.getOrders();
      setOrders(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les commandes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (orderId, type) => {
    try {
      const { ordersAPI } = await import('../../api');
      await ordersAPI.downloadFile(orderId, type);
      toast({
        title: "Téléchargement démarré",
        description: "Le fichier est en cours de téléchargement",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Terminé
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30">
            <Clock className="h-3 w-3 mr-1" />
            En cours
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
            <AlertCircle className="h-3 w-3 mr-1" />
            En attente
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <XCircle className="h-3 w-3 mr-1" />
            Annulé
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30">
            {status}
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              Mes <span className="text-[#C00000]">Commandes</span>
            </h1>
            <p className="text-[#A6A6A6]">Suivez l'état de vos commandes de tuning files</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#A6A6A6]">Chargement...</p>
            </div>
          ) : orders.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="py-12">
                <div className="text-center">
                  <FileText className="h-12 w-12 text-[#A6A6A6] mx-auto mb-4" />
                  <p className="text-[#A6A6A6] mb-4">Aucune commande pour le moment</p>
                  <Button 
                    onClick={() => window.location.href = '/client/new-order'}
                    className="bg-[#C00000] hover:bg-[#C00000]/90"
                  >
                    Créer une commande
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {orders.map((order) => (
                <Card 
                  key={order.id} 
                  className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/client/orders/${order.order_id}`)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-white mb-2">
                          {order.vehicle_info.brand} {order.vehicle_info.model} {order.vehicle_info.engine}
                        </CardTitle>
                        <CardDescription className="text-[#A6A6A6]">
                          Commande {order.order_id} - {order.service}
                        </CardDescription>
                        {order.engineer_name && (
                          <div className="flex items-center gap-2 mt-2">
                            <User className="h-4 w-4 text-[#C00000]" />
                            <span className="text-[#C00000] text-sm font-semibold">
                              Traité par : {order.engineer_name}
                            </span>
                          </div>
                        )}
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Date</p>
                          <p className="text-white font-semibold">
                            {formatDate(order.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Prix</p>
                          <p className="text-white font-semibold">{order.price}€ HT</p>
                        </div>
                        <div>
                          <p className="text-[#A6A6A6] text-sm mb-1">Fichier original</p>
                          <p className="text-white font-semibold text-sm truncate">{order.original_file_name || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#A6A6A6]/20">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/client/orders/${order.order_id}`);
                          }}
                          className="bg-[#C00000] hover:bg-[#C00000]/90"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Voir les détails
                        </Button>
                        {order.modified_file_name && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(order.order_id, 'modified');
                            }}
                            variant="outline"
                            className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Télécharger le fichier
                          </Button>
                        )}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                            setShowChat(true);
                          }}
                          variant="outline"
                          className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Chat
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <ChatBox 
              orderId={selectedOrder.order_id}
              onClose={() => setShowChat(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
